import { ShieldOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FILL_GRADIENT, LINE_GRADIENT } from '../../constants/gradients';

interface AccessDeniedPanelProps {
  projectId: string;
  errorMessage?: string;
}

export default function AccessDeniedPanel({ projectId }: AccessDeniedPanelProps) {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex items-center justify-center p-4 relative overflow-hidden">
      {/* Giant faded 403 in background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none -mt-64">
        <span className="text-[32rem] font-black text-gray-200 dark:text-gray-800/30 leading-none">
          403
        </span>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
        className="max-w-lg w-full relative z-10"
      >
        <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl p-8 md:p-12 text-center border border-gray-200 dark:border-gray-800">
          {/* Icon */}
          <motion.div 
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.1, duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="w-20 h-20 mx-auto mb-6 flex items-center justify-center"
          >
            <ShieldOff className="w-16 h-16 text-[#FF00CF]" strokeWidth={1.5} />
          </motion.div>
          
          {/* Title */}
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
            Access Denied
          </h2>
          
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
            You don't have access to this project.
          </p>
          
          {/* Action button */}
          <button
            onClick={() => navigate('/projects')}
            className="w-full px-6 py-3.5 text-white font-semibold rounded-xl transition-opacity hover:opacity-90"
            style={{ background: FILL_GRADIENT }}
          >
            Go to My Projects
          </button>
          
          {/* Project ID footer */}
          {projectId && (
            <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-800">
              <p className="text-xs text-gray-400 dark:text-gray-600 font-mono">
                Project ID: {projectId}
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
